import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, LogOut, MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import Logo from "@/components/shared/Logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { clearSignedInBrowserState } from "@/lib/browser-logout-state";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import { openMinSupportChat, saveUiLanguage } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { storePreferencesEntryTelemetry } from "@/lib/preferences-entry-telemetry";
import { resetBrowserSupabaseSession } from "@/lib/supabase-session";
import { cn } from "@/lib/utils";

const SECTION_ORDER = ["preferences", "referrals", "account", "billing"];
const SECTION_LABELS = {
  preferences: "Preferences",
  referrals: "Referrals",
  account: "Account",
  billing: "Billing",
};

function preloadSectionRoute(href = "") {
  const normalizedHref = String(href || "").trim();
  if (!normalizedHref) {
    return Promise.resolve();
  }
  if (normalizedHref.startsWith("/setup")) {
    return import("@/pages/Setup");
  }
  if (normalizedHref.startsWith("/account")) {
    return import("@/pages/Account");
  }
  if (normalizedHref.startsWith("/referrals")) {
    return import("@/pages/Referrals");
  }
  if (normalizedHref.startsWith("/billing")) {
    return import("@/pages/Billing");
  }
  return Promise.resolve();
}

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function PlanSummaryBadge({
  planLabel,
  tooltipCopy = "",
  tooltipLoading = false,
  href = "",
}) {
  const { tx } = useI18n();
  const navigate = useNavigate();
  const displayPlanLabel = String(planLabel || "").trim().replace(/\s+plan$/i, "") || planLabel;

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <span className="inline-flex">
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={tooltipLoading ? tx("Loading billing details") : tx(tooltipCopy)}
              data-testid="settings-plan-trigger"
              className="inline-flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={() => {
                if (!href) {
                  return;
                }
                navigate(href);
              }}
            >
              <Badge
                variant="outline"
                className="inline-flex h-9 items-center rounded-full border-border/80 bg-background px-3 text-xs font-semibold uppercase tracking-normal text-foreground max-[559px]:h-8 max-[559px]:px-2.5"
              >
                {tx(displayPlanLabel)}
              </Badge>
            </button>
          </TooltipTrigger>
          <TooltipContent
            data-testid="settings-plan-tooltip"
            side="bottom"
            align="end"
            sideOffset={10}
            className="max-w-64 border border-border/80 bg-background px-3 py-2 text-left text-[13px] leading-relaxed text-foreground shadow-xl shadow-black/10"
          >
            <p>{tooltipLoading ? tx("Loading billing details...") : tx(tooltipCopy)}</p>
          </TooltipContent>
        </span>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function SignedInSettingsShell({
  currentSection,
  title,
  onBack,
  planLabel,
  planTooltipCopy,
  planTooltipLoading = false,
  sectionLinks = {},
  headerNavAccessory = null,
  children,
  contentClassName = "",
}) {
  const { tx } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const shellTitle = String(title || SECTION_LABELS[currentSection] || "Settings").trim();
  const sectionOptions = SECTION_ORDER
    .filter((section) => section !== currentSection && sectionLinks[section])
    .map((section) => ({
      key: section,
      label: SECTION_LABELS[section],
      href: sectionLinks[section],
    }));
  const planHref = currentSection === "billing"
    ? `${location.pathname}${location.search}`
    : String(sectionLinks.billing || "").trim();

  useEffect(() => {
    for (const section of sectionOptions) {
      void preloadSectionRoute(section.href);
    }
  }, [sectionOptions]);

  async function handleLogout() {
    try {
      setLoggingOut(true);
      await resetBrowserSupabaseSession();
      clearSignedInBrowserState({
        preserveAttributionCode: false,
        preserveShareLinkEntry: false,
      });
      navigate("/", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="border-b border-border/70" data-testid="signed-in-settings-header">
        <div className="mx-auto max-w-6xl px-4 py-4 sm:px-5">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-fit rounded-xl px-3 text-muted-foreground"
                onClick={onBack}
              >
                <ArrowLeft data-icon="inline-start" />
                {tx("Back")}
              </Button>
            </div>
            <div className="justify-self-center">
              <span className="block max-w-[10rem] truncate text-center font-display text-base font-bold tracking-tight text-foreground min-[560px]:hidden">
                {tx(shellTitle)}
              </span>
              <button
                type="button"
                className="hidden rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background min-[560px]:inline-flex"
                onClick={() => navigate("/")}
                aria-label={tx("Go to home page")}
                data-testid="settings-home-button"
              >
                <Logo size="sm" />
              </button>
            </div>
            <div className="flex items-center justify-self-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 rounded-xl px-3 text-muted-foreground max-[559px]:w-9 max-[559px]:px-0"
                onClick={() => void handleLogout()}
                disabled={loggingOut}
                aria-label={loggingOut ? tx("Logging out") : tx("Log out")}
                data-testid="settings-logout-button"
              >
                {loggingOut ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="max-[559px]:hidden">{tx("Logging out")}</span>
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4" />
                    <span className="max-[559px]:hidden">{tx("Log out")}</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 min-[560px]:flex-row min-[560px]:items-center min-[560px]:justify-between">
            <div className="order-2 -mx-4 overflow-x-auto px-4 pb-1 min-[560px]:order-1 min-[560px]:mx-0 min-[560px]:min-w-0 min-[560px]:overflow-visible min-[560px]:px-0 min-[560px]:pb-0">
              <div className="flex w-max items-center gap-2 min-[560px]:w-auto min-[560px]:min-w-0">
                {sectionOptions.map((section) => (
                  <Button
                    key={section.key}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 rounded-full border border-border/70 px-4 text-sm text-foreground"
                    onMouseEnter={() => {
                      void preloadSectionRoute(section.href);
                    }}
                    onFocus={() => {
                      void preloadSectionRoute(section.href);
                    }}
                    onTouchStart={() => {
                      void preloadSectionRoute(section.href);
                    }}
                    onClick={() => {
                      if (section.key === "preferences") {
                        storePreferencesEntryTelemetry({
                          entryPath: currentSection,
                          startedAt: nowMs(),
                          destination: "preferences",
                          usedSingleLoader: true,
                        });
                      }
                      navigate(section.href);
                    }}
                  >
                    {tx(section.label)}
                  </Button>
                ))}
                {headerNavAccessory ? (
                  <div className="shrink-0">
                    {headerNavAccessory}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="order-1 flex shrink-0 items-center justify-end gap-2 min-[560px]:order-2">
              <LanguageSwitcher
                compactOnMobile
                fitContent
                className="max-[559px]:max-w-[5.4rem]"
                onChange={(nextLanguageCode) => {
                  void saveUiLanguage(nextLanguageCode).catch(() => {});
                }}
              />
              <PlanSummaryBadge
                planLabel={planLabel}
                tooltipCopy={planTooltipCopy}
                tooltipLoading={planTooltipLoading}
                href={planHref}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 rounded-full border border-border/70 px-3 text-xs font-semibold tracking-normal text-foreground hover:bg-muted/60 max-[559px]:h-8 max-[559px]:w-10 max-[559px]:px-0"
                onClick={() => {
                  void openMinSupportChat({
                    sourceStep: `settings_${currentSection}_support`,
                  });
                }}
                aria-label={tx("Support")}
                data-testid="settings-support-button"
              >
                <MessageCircle className="h-4 w-4 shrink-0" />
                <span className="max-[559px]:hidden">{tx("Support")}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className={cn("px-4 pb-20 pt-4 sm:px-5 md:pb-16", contentClassName)}>
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          {children}
        </div>
      </main>
    </div>
  );
}
